import React, { useState } from "react";
import { Headphones, Upload, Trash2, Loader2, Play } from "lucide-react";
import { jwtDecode } from "jwt-decode";
import axiosClient from "../../api/axiosClient";
import {
  computeNextIndex,
  isAudioFile,
  formatRecordingLabel,
  TYPE_LABELS,
} from "./recordingsUtils";

/**
 * LeadRecordings
 * Bloque de grabaciones de visitas/llamadas de un lead.
 * - Sube el audio directo a S3 con presigned URL (soporta grabaciones largas).
 * - Persiste el array `recordings` del contacto inmediatamente en bulk-contacts.
 * - Reproduce firmando la URL de descarga bajo demanda (las firmadas caducan).
 *
 * Props:
 *  - contactId: string|null. Si null, el lead aún no está guardado.
 *  - recordings: array. Lista actual de grabaciones.
 *  - onChange(nextRecordings): callback al padre tras subir/borrar.
 */
export default function LeadRecordings({ contactId, recordings, onChange }) {
  const list = Array.isArray(recordings) ? recordings : [];
  const [type, setType] = useState("visita");
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [playUrl, setPlayUrl] = useState(null);
  const [loadingPlayId, setLoadingPlayId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const me = (() => {
    try {
      const tok = localStorage.getItem("token");
      return tok ? jwtDecode(tok) : null;
    } catch {
      return null;
    }
  })();

  const persist = async (nextRecordings) => {
    await axiosClient.post("/ventas/bulk-contacts", {
      contact_id: contactId,
      recordings: nextRecordings,
    });
    onChange?.(nextRecordings);
  };

  // Subida desde el input de archivo (botón / grabar en móvil).
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permitir re-subir el mismo archivo
    uploadFile(file);
  };

  // Subida soltando un archivo en la zona de arrastrar y soltar.
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading || !contactId) return;
    uploadFile(e.dataTransfer?.files?.[0]);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setError("");

    if (!isAudioFile(file.name, file.type)) {
      setError("El archivo debe ser un audio (m4a, mp3, ogg, wav, amr…).");
      return;
    }
    if (!contactId) {
      setError("Guarda el lead antes de subir una grabación.");
      return;
    }

    const contentType = file.type || "audio/mpeg";
    setUploading(true);
    try {
      // 1. Presigned PUT
      const pres = await axiosClient.post("/upload-file", {
        getPresignedUrl: true,
        fileName: file.name,
        contentType,
      });
      if (!pres?.uploadUrl || !pres?.s3Key) {
        throw new Error("Respuesta de subida incompleta");
      }
      // 2. PUT directo a S3
      const putRes = await fetch(pres.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": contentType },
      });
      if (!putRes.ok) {
        throw new Error(`S3 respondió ${putRes.status}`);
      }
      // 3. Append + persistir
      const senderRaw =
        (me?.full_name || me?.user_id || "").split("@")[0] || "";
      const senderName = senderRaw
        ? senderRaw.charAt(0).toUpperCase() + senderRaw.slice(1)
        : "";
      const entry = {
        id:
          (typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `rec_${Date.now()}_${Math.random().toString(36).slice(2)}`),
        type,
        index: computeNextIndex(list, type),
        s3_key: pres.s3Key,
        file_name: file.name,
        content_type: contentType,
        note: note.trim() || "",
        uploaded_at: new Date().toISOString(),
        uploaded_by: me?.user_id || "",
        uploaded_by_name: senderName,
      };
      await persist([...list, entry]);
      setNote("");
    } catch (err) {
      console.error("[LeadRecordings] subida fallida:", err);
      setError("No se pudo subir la grabación. Inténtalo de nuevo.");
    } finally {
      setUploading(false);
    }
  };

  const handlePlay = async (rec) => {
    if (playingId === rec.id) {
      setPlayingId(null);
      setPlayUrl(null);
      return;
    }
    setLoadingPlayId(rec.id);
    try {
      const data = await axiosClient.post("/upload-file", {
        getDownloadUrl: true,
        s3Key: rec.s3_key,
      });
      if (!data?.url) throw new Error("Sin URL de descarga");
      setPlayUrl(data.url);
      setPlayingId(rec.id);
    } catch (err) {
      console.error("[LeadRecordings] firma de descarga fallida:", err);
      setError("No se pudo cargar la grabación.");
    } finally {
      setLoadingPlayId(null);
    }
  };

  const handleDelete = async (rec) => {
    if (!window.confirm(`¿Eliminar "${formatRecordingLabel(rec)}"?`)) return;
    setDeletingId(rec.id);
    try {
      const next = list.filter((r) => r.id !== rec.id);
      await persist(next);
      if (playingId === rec.id) {
        setPlayingId(null);
        setPlayUrl(null);
      }
    } catch (err) {
      console.error("[LeadRecordings] borrado fallido:", err);
      setError("No se pudo eliminar la grabación.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <label className="block text-xs font-black text-emerald-700 uppercase tracking-wider mb-2.5">
        Grabaciones de visitas/llamadas
      </label>

      {!contactId && (
        <p className="text-xs text-amber-600 font-semibold mb-3">
          Guarda el lead para poder subir grabaciones.
        </p>
      )}

      {/* Controles de subida */}
      <div className="rounded-2xl border border-emerald-200 bg-white/60 p-4 space-y-3">
        <div className="flex gap-2">
          {Object.keys(TYPE_LABELS).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setType(k)}
              className={`flex-1 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                type === k
                  ? "bg-emerald-500 text-white shadow-md"
                  : "bg-white border border-emerald-200 text-slate-600 hover:bg-emerald-50"
              }`}
            >
              {TYPE_LABELS[k]}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Nota (opcional): contexto de la grabación…"
          className="w-full px-4 py-2.5 rounded-xl border border-emerald-200 bg-white/50 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none font-semibold text-slate-700 text-sm"
        />
        {/* Zona de subida: arrastrar y soltar o hacer clic para elegir/grabar */}
        <label
          onDragOver={(e) => {
            if (uploading || !contactId) return;
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`flex flex-col items-center justify-center gap-1 py-4 px-3 rounded-xl border-2 border-dashed text-center transition-all ${
            uploading || !contactId
              ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"
              : dragOver
                ? "bg-emerald-100 border-emerald-400 text-emerald-700 cursor-pointer"
                : "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 cursor-pointer active:scale-95"
          }`}
        >
          <span className="flex items-center gap-2 font-black text-xs uppercase tracking-wider">
            {uploading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Upload size={14} />
            )}
            {uploading ? "Subiendo…" : `Subir ${TYPE_LABELS[type]}`}
          </span>
          {!uploading && (
            <span className="text-[11px] font-semibold normal-case tracking-normal text-emerald-600/80">
              {dragOver
                ? "Suelta el audio aquí"
                : "Arrastra el audio aquí o haz clic para elegir o grabar"}
            </span>
          )}
          <input
            type="file"
            accept="audio/*"
            capture
            className="hidden"
            disabled={uploading || !contactId}
            onChange={handleFile}
          />
        </label>
        {error && (
          <p className="text-xs text-red-600 font-semibold">{error}</p>
        )}
      </div>

      {/* Lista de grabaciones */}
      {list.length > 0 && (
        <div className="mt-3 space-y-2">
          {list.map((rec) => (
            <div
              key={rec.id}
              className="rounded-xl border border-slate-200 bg-white/70 p-3"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                    <Headphones size={14} className="text-emerald-600" />
                    {formatRecordingLabel(rec)}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {rec.uploaded_at
                      ? new Date(rec.uploaded_at).toLocaleDateString("es-ES", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })
                      : ""}
                    {rec.uploaded_by_name ? ` · ${rec.uploaded_by_name}` : ""}
                  </p>
                  {rec.note && (
                    <p className="text-[11px] text-slate-400 italic mt-0.5 line-clamp-2">
                      {rec.note}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handlePlay(rec)}
                    className="p-2 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-all active:scale-95"
                    title={playingId === rec.id ? "Ocultar" : "Reproducir"}
                  >
                    {loadingPlayId === rec.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Play size={14} />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(rec)}
                    disabled={deletingId === rec.id}
                    className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-all active:scale-95 disabled:opacity-50"
                    title="Eliminar"
                  >
                    {deletingId === rec.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </button>
                </div>
              </div>
              {playingId === rec.id && playUrl && (
                <audio
                  src={playUrl}
                  controls
                  autoPlay
                  className="w-full mt-2"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
